import React, { useEffect, useState } from "react";
import "../assets/css/pages/PrivacyPolicy.css";
import { useLocation } from "react-router-dom";
import { fetchPageByDynamicRouting } from "../api/pages";
import Lenis from "@studio-freight/lenis";
import { useSeo } from "../contexts/SeoContext";


/**
 * Smooth scroll using Lenis
 * - Same pattern as Home.jsx and About.jsx
 * - Runs once on mount
 */
const useLenis = () => {
    useEffect(() => {
        if (typeof window === "undefined") return;

        // Disable native smooth scroll to avoid conflicts with Lenis
        document.documentElement.style.scrollBehavior = "auto";

        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => 1 - Math.pow(1 - t, 3), // ease-out
            smoothWheel: true,
            smoothTouch: false,
        });

        // Store Lenis instance globally for PageTransition to access
        window.__lenis = lenis;

        const raf = (time) => {
            lenis.raf(time);
            requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);

        return () => {
            lenis.destroy();
            if (window.__lenis === lenis) {
                delete window.__lenis;
            }
        };
    }, []);
};

// Enhanced AOS (Animate On Scroll) hook with stagger support
const useAOS = (pageData, loading) => {
    useEffect(() => {
        if (!pageData || loading) return;

        let observer = null;
        const observedElements = [];

        const timer = setTimeout(() => {
            const observerOptions = {
                threshold: 0.15,
                rootMargin: "0px 0px -100px 0px",
            };

            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    requestAnimationFrame(() => {
                        if (entry.isIntersecting) {
                            const section = entry.target;

                            // Animate the section itself
                            if (!section.classList.contains("aos-animate")) {
                                section.classList.add("aos-animate");
                            }

                            // Trigger stagger animation for all child elements in this section
                            const staggerElements =
                                section.querySelectorAll("[data-stagger-index]");
                            staggerElements.forEach((el, idx) => {
                                setTimeout(() => {
                                    el.classList.add("stagger-animate");
                                }, idx * 10);
                            });
                        }
                    });
                });
            }, observerOptions);

            // Observe all sections with AOS classes
            const sectionsToObserve =
                document.querySelectorAll(".aos-fade-up");

            sectionsToObserve.forEach((element) => {
                if (!element.classList.contains("aos-animate")) {
                    // Check if section is already in view on page load
                    const rect = element.getBoundingClientRect();
                    const isInView =
                        rect.top < window.innerHeight * 0.8 && rect.bottom > 0;

                    if (isInView) {
                        // Section is already visible, trigger animation immediately
                        element.classList.add("aos-animate");
                        const staggerElements =
                            element.querySelectorAll("[data-stagger-index]");
                        staggerElements.forEach((el, idx) => {
                            setTimeout(() => {
                                el.classList.add("stagger-animate");
                            }, idx * 10);
                        });
                    } else {
                        // Section not in view, observe it
                        observer.observe(element);
                        observedElements.push(element);
                    }
                }
            });
        }, 100);

        return () => {
            clearTimeout(timer);
            if (observer) {
                observedElements.forEach((element) => {
                    if (element) observer.unobserve(element);
                });
            }
        };
    }, [pageData, loading]);
};

// Function to clean HTML content - remove &nbsp; and other unwanted entities
const cleanHtmlContent = (html) => {
    if (!html || typeof html !== "string") return "";
    
    return html
        .replace(/&nbsp;/g, " ") // Replace &nbsp; with regular space
        .replace(/&nbsp/g, " ") // Replace &nbsp (without semicolon) with space
        .replace(/\s+/g, " ") // Replace multiple spaces with single space
        .replace(/>\s+</g, "><") // Remove spaces between tags
        .trim();
};

export default function PrivacyPolicy() {
    const location = useLocation();
    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setPageSeo } = useSeo();
    
    // Extract slug from route path
    const slug = location.pathname.replace(/^\/+/, "");

    // 🔥 Initialize Lenis smooth scroll
    useLenis();

    // Initialize AOS animations
    useAOS(pageData, loading);

    useEffect(() => {
        let isMounted = true;

        const loadPage = async () => {
            setLoading(true);
            try {
                const res = await fetchPageByDynamicRouting(slug);
                if (!isMounted) return;

                setPageData(res);
                
                // Update SEO data
                if (res?.seo) {
                    setPageSeo(res.seo);
                }
                
                setError(null);
                setLoading(false);
            } catch (err) {
                if (!isMounted) return;
                setError("Unable to load the privacy policy at the moment.");
                setLoading(false);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadPage();

        return () => {
            isMounted = false;
        };
    }, [slug, setPageSeo]);

    useEffect(() => {
        if (pageData?.page_title && pageData.page_title.trim() !== "") {
            document.title = `${pageData.page_title} | Vancouver Lookout`;
        } else {
            document.title = "Privacy Policy | Vancouver Lookout";
        }
    }, [pageData]);

    // Always render page structure - show content immediately when available
    if (error || !pageData) {
        return (
            <div className="page-error">
                {/* {error || "No privacy policy content available."} */}
            </div>
        );
    }

    return (
        <div className="privacy-policy-page">
            <div className="canvas">
                <div className="privacy-policy-content">
                    {pageData?.page_title && pageData.page_title.trim() !== "" && (
                        <h1 className="privacy-policy-title aos-fade-up" data-stagger-index="0">
                            {pageData.page_title}
                        </h1>
                    )}

                    {/* <div className="breadcrumb">
                        <span>Home</span> <span className="separator">›</span> <span>About</span> <span className="separator">›</span> <span>Privacy Policy</span>
                    </div> */}

                    {pageData?.page_content && pageData.page_content.trim() !== "" && (
                        <div
                            className="privacy-policy-text aos-fade-up"
                            data-stagger-index="1"
                            dangerouslySetInnerHTML={{ __html: cleanHtmlContent(pageData.page_content) }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

